import {
  ADMIN_DELETE_USER_URL,
  ADMIN_GET_USERS,
  authFetcher,
  CHALLENGE_URL,
  DELETE_URL,
  DEV_URL,
  EMAIL_URL,
  fetchWithAuth,
  LOGIN_URL,
  REGISTER_URL,
  RESET_URL,
  TOKEN_URL,
  VERIFY_URL,
} from "./const.routes.ts";
import { fetchHandler } from "./fetch";
import useSWR from "swr";
import { ERROR_TEXT, ErrorType } from "../types/Error/Errors.ts";
import { err, Result } from "../types/Result.ts";
import { AccountResponse, PwdReqType, RegisterType } from "../types/Login.ts";

export function useAdminUsers(fallback: string[]) {
  const { data, error, isLoading } = useSWR<string[], Error>(
    ADMIN_GET_USERS,
    authFetcher,
  );
  return {
    users: data ?? fallback,
    isLoading,
    isError: error,
  };
}

export function useFetchEmail(fallback: string) {
  const { data, error, isLoading } = useSWR<string, Error>(
    EMAIL_URL,
    authFetcher,
  );
  return {
    email: data ?? fallback,
    isLoading,
    isError: error,
  };
}

/**
 * Send email, password, get the session-token or error back
 * @param email
 * @param password
 */
export async function fetchLogin(email: string, password: string) {
  return await fetchHandler<string>(
    fetch(LOGIN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email, password: password }),
    }),
  );
}

/**
 * Send email, password, get the session-token or error back
 * @param email
 * @param password
 */
export async function getChallenge() {
  return await fetchHandler<{ challenge: string; difficulty: number }>(
    fetch(CHALLENGE_URL, {
      method: "GET",
    }),
  );
}

export async function fetchDev(
  devCode: string,
): Promise<Result<boolean, string>> {
  try {
    const auth_response = await fetch(DEV_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ devCode }),
    });
    return { ok: true, value: auth_response.ok };
  } catch (error) {
    console.error(error);
    return err(ERROR_TEXT.FETCH);
  }
}

export async function fetchToken(
  authToken: string,
): Promise<Result<string, string>> {
  return await fetchHandler(
    fetch(TOKEN_URL, {
      method: "GET",
      headers: {
        Authorization: authToken ? `Bearer ${authToken}` : "",

        "Content-Type": "application/json",
      },
    }),
  );
}

export async function fetchVerify(
  otp: string,
  challenge: string,
  nonce: number,
): Promise<Result<string, string>> {
  return await fetchHandler(
    fetchWithAuth(`${VERIFY_URL}otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ otp: otp, challenge: challenge, nonce: nonce }),
    }),
  );
}

export async function fetchCreateVerify() {
  return await fetchHandler(fetchWithAuth(`${VERIFY_URL}mail`));
}

export async function fetchCreatePasswordReset(
  email: string,
  challenge: string,
  nonce: number,
): Promise<Result<string, ErrorType>> {
  return await fetchHandler(
    fetch(`${RESET_URL}request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email,
        challenge: challenge,
        nonce: nonce,
      }),
    }),
  );
}

export async function fetchPostPassword(
  payload: PwdReqType,
): Promise<Result<string, ErrorType>> {
  return await fetchHandler(
    fetch(`${RESET_URL}password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),
  );
}

export async function fetchRegister(data: RegisterType) {
  return await fetchHandler<string>(
    fetch(REGISTER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }),
  );
}

export async function deleteAll(password: string) {
  return await fetchHandler<AccountResponse>(
    fetchWithAuth(DELETE_URL, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password: password }),
    }),
  );
}

export async function deleteUserByAdmin(password: string, userId: string) {
  return await fetchHandler<AccountResponse>(
    fetchWithAuth(ADMIN_DELETE_USER_URL(userId), {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password: password }),
    }),
  );
}
